import { useQueryClient } from '@tanstack/react-query'
import { createContext, ReactNode, use, useMemo } from 'react'
import useWebSocket, { ReadyState, SendMessage } from 'react-use-websocket'

import env from '~/env'
import { selectActivePlanningDomainUuid } from '~/store/slices/userSlice'
import { useStore } from '~/store/store'
import { expireSession } from '~/utils/session'

import { addEvents } from './handleWebSocketUpdates'
import { isCustomHandlerType, useWebSocketHandlers } from './useWebSocketHandlers'

const connectionStatusMap = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
} as const

type ConnectionStatus = (typeof connectionStatusMap)[keyof typeof connectionStatusMap]

type MessageHandler = (data: unknown) => void

type WebSocketContextValue = {
    sendMessage: SendMessage
    readyState: ReadyState
    connectionStatus: ConnectionStatus
    registerMessageHandler: (type: string, handler: MessageHandler) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

function createSocketUrl(accessToken: string | null, socketUrl: string) {
    // Keep creation outside the component so React Compiler cannot hoist the
    // constant-URL callback across token renewals.
    // A timer task keeps react-use-websocket's flushSync outside lifecycle replay.
    return accessToken ? () => new Promise<string>(resolve => setTimeout(() => resolve(socketUrl), 0)) : null
}

function isAuthExpiryClose(event: CloseEvent) {
    return event.code === 1008 && event.reason === 'AUTH_EXPIRED'
}

export function WebSocketProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient()
    const apiAccessToken = useStore(state => state.user.apiAccessToken)
    const { registerMessageHandler, handleCustomMessage } = useWebSocketHandlers()
    const activePlanningDomainUuid = useStore(selectActivePlanningDomainUuid)

    // Browsers cannot set headers on a websocket handshake, so the active planning domain goes in the query.
    const planningDomainQuery = activePlanningDomainUuid === null ? '' : `?planning_domain=${activePlanningDomainUuid}`
    const socketUrl = env.VITE_API_BASE_URL.replace('https', 'wss') + '/ws' + planningDomainQuery
    // react-use-websocket reconnects when callback identity changes, using the refreshed auth cookie.
    // Explicit useMemo ties this behavior to token renewal or domain changes, not unrelated renders, even without
    // React Compiler (e.g. in unit tests). The token stays out of the URL.
    const getSocketUrl = useMemo(() => createSocketUrl(apiAccessToken, socketUrl), [apiAccessToken, socketUrl])

    const handleMessage = (data: string) => {
        if (data === 'pong') return

        try {
            const message = JSON.parse(data)

            if (isCustomHandlerType(message)) handleCustomMessage(message)
            else addEvents(data, queryClient)
        } catch (error) {
            console.error('Expected JSON for WebSocket event, received:', error)
            return
        }
    }

    const { sendMessage, readyState } = useWebSocket(getSocketUrl, {
        share: true,
        onMessage: event => handleMessage(event.data),
        onClose: event => {
            if (!env.VITE_FF_REFRESH_TOKEN && isAuthExpiryClose(event)) expireSession()
        },
        // Expired credentials need renewal, not another handshake with the same cookie.
        shouldReconnect: event => !isAuthExpiryClose(event),
        heartbeat: env.VITE_HINT_WEBSOCKET
            ? {
                  interval: 5000,
                  message: 'ping',
                  returnMessage: 'pong',
                  timeout: 10000,
              }
            : false,
    })

    const value: WebSocketContextValue = {
        sendMessage,
        readyState,
        connectionStatus: connectionStatusMap[readyState],
        registerMessageHandler,
    }

    return <WebSocketContext value={value}>{children}</WebSocketContext>
}

export function useWebSocketConnection(): WebSocketContextValue {
    const context = use(WebSocketContext)
    if (!context) {
        throw new Error('useWebSocketConnection must be used within a WebSocketProvider')
    }
    return context
}
